/**
 * Stream of data that can be read from and written to.
 * Internally used by {@link Port}.
 * @internal
 */
export class Stream<T> {
  private readonly _sinks = new Set<ReadableStreamDefaultController<T>>()
  private readonly _source = new WritableStream<T>({
    write: (data) => {
      this._sinks.forEach((sink) => sink.enqueue(data))
    },
    close: () => {
      this._sinks.forEach((sink) => sink.close())
      this._sinks.clear()
    },
  })

  /**
   * @returns writer - a way to write data to the Stream.
   *
   * Only one writer can be used at a time.
   * Call `writer.releaseLock()` to release the writer and be able to `.getWriter()` again.
   */
  getWriter() {
    return this._source.getWriter()
  }

  /**
   * @returns reader - a way to read data from the Stream.
   *
   * Multiple readers can be used at the same time.
   * When you have done with the reader, call `reader.cancel()` to release resources held by the reader.
   */
  getReader() {
    let controller: ReadableStreamDefaultController<T>

    const readable = new ReadableStream<T>({
      start: (ctrl) => {
        this._sinks.add((controller = ctrl))
      },
      cancel: () => {
        this._sinks.delete(controller)
      },
    })

    return readable.getReader()
  }

  /**
   * Pipes the stream data to another stream.
   * @returns an "unpipe" function that can be called to cancel the pipe.
   */
  pipe(stream: Stream<T>) {
    const writer = stream.getWriter()
    const reader = this.getReader()
    const stop = () => {
      reader.cancel()
      writer.releaseLock()
    }

    ;(async () => {
      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          stop()
          break
        }

        writer.write(value)
      }
    })()

    return stop
  }

  /** Closes the stream terminating all the stream‘s writers and readers */
  close() {
    this._sinks.forEach((sink) => sink.close())
    this._sinks.clear()

    if (!this._source.locked) this._source.close()
  }
}
